// Phase-16 R8 — Mobile bottom navigation.
//
// Audit B-06 finding: sidenav collapsed to a horizontal chip-row on
// <md which lost hierarchy and discoverability. Replace with a fixed
// 5-slot bottom bar that follows iOS/Android conventions:
//
//   - h-16 fixed bottom-0 inset-x-0 (full width)
//   - safe-area-inset-bottom respected (env())
//   - role-aware slot table (see SLOT_TABLE)
//   - hidden at >=md via md:hidden
//   - hidden on /classroom (full-screen video workflow) and all auth
//     routes (login/register/forgot have their own shell)
//   - 48×48 minimum touch target per slot (WCAG 2.5.5)
//   - active slot gets a 2-px top border in --accent
//
// Mount once in router.tsx Layout, after Footer. The component
// returns null on routes where it shouldn't show so the parent
// doesn't need a conditional.
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useRole, ROLES, type RoleId } from "../role";
import { useCurrentRoute, useGo, type Go } from "../router";
import { cn } from "./utils";

interface NavSlot {
  /** Route ID that `go(...)` accepts. */
  to: string;
  /**
   * S-FIX-BUNDLE-4 / R2-007 — کلیدِ i18n در namespaceِ `shell` (نه متنِ فارسی).
   * `shell` پیش از اولین پِینت بارگذاری می‌شود (`i18n/route-namespaces.ts`)، پس
   * نوارِ پایین حتی روی شبکهٔ کند هم کلیدِ خام نشان نمی‌دهد.
   */
  labelKey: string;
  /** Slot icon name from our existing Icon set, or inline SVG fallback. */
  iconPath: string;
}

const ICONS: Record<string, string> = {
  home: "M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-5h-4v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V11z",
  book: "M4 4h6a2 2 0 0 1 2 2v14a3 3 0 0 0-3-3H4V4zM20 4h-6a2 2 0 0 0-2 2v14a3 3 0 0 1 3-3h5V4z",
  live: "M12 1a4 4 0 0 1 4 4v3a4 4 0 1 1-8 0V5a4 4 0 0 1 4-4zM5 8a7 7 0 0 0 14 0M12 15v6M9 21h6",
  chat: "M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-5l-5 4v-4H6a3 3 0 0 1-3-3V6z",
  graph: "M3 3v18h18M7 14l4-4 4 4 5-6",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0H4z",
  shield: "M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z",
  audit: "M9 2h6l4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM9 12h6M9 16h6M9 8h4",
  inbox: "M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z",
  more: "M5 12a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm7 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm7 0a2 2 0 1 1 0 4 2 2 0 0 1 0-4z",
  community: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  schools: "M3 21V9l9-6 9 6v12M9 21V12h6v9",
  laptop: "M3 4h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM2 21h20",
};

// Per-role slot table. Keep ≤5 slots per role for thumb-reach UX.
//
// The mapping deliberately favours the user's *primary* workflows:
//   - Student: learn + practice + AI tutor
//   - Instructor: teaching + grading + content + analytics
//   - Admin / super_admin: ops + audit + analytics
//   - TA: lighter teach workflow
//   - Content manager: catalog + authoring + analytics
//   - Support / moderator: messages + audit + help
//   - Parent: child progress + inbox
//   - Org: cohort + analytics + catalog
// S-21 — exported for the nav-coherence vitest: every slot's `to` must be a
// registered route the role is permitted to open (slot 0 is rewritten to the
// role's homeRoute at render, so the test checks homeRoute for it).
export const SLOT_TABLE: Record<RoleId, NavSlot[]> = {
  finance_manager: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "admin/finance/treasury", labelKey: "bottomNav.slot.treasury", iconPath: ICONS.graph },
    { to: "admin/finance/payroll", labelKey: "bottomNav.slot.payroll", iconPath: ICONS.shield },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  student: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "my-courses", labelKey: "bottomNav.slot.courses", iconPath: ICONS.book },
    { to: "classroom", labelKey: "bottomNav.slot.liveClass", iconPath: ICONS.live },
    { to: "tutor", labelKey: "bottomNav.slot.tutor", iconPath: ICONS.chat },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // Slot 0 is rewritten to the role's homeRoute by the override below; slots 1-3
  // must be REAL routes the role's desktop sidebar uses (not the retired mock
  // /instructor /authoring /analytics pages) and that route-roles.ts permits.
  instructor: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "my-courses", labelKey: "bottomNav.slot.myCourses", iconPath: ICONS.book },
    { to: "admin/course-content", labelKey: "bottomNav.slot.content", iconPath: ICONS.laptop },
    { to: "gradebook", labelKey: "bottomNav.slot.grades", iconPath: ICONS.graph },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  admin: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "admin", labelKey: "bottomNav.slot.admin", iconPath: ICONS.shield },
    { to: "audit", labelKey: "bottomNav.slot.audit", iconPath: ICONS.audit },
    { to: "analytics", labelKey: "bottomNav.slot.analytics", iconPath: ICONS.graph },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // R-NAV-2 — faculty_admin (scope-bound academic ops). Home slot points at
  // the faculty dashboard; R-NAV-4 reworks the home slot to homeRoute globally.
  faculty_admin: [
    { to: "faculty-dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "admin/departments", labelKey: "bottomNav.slot.departments", iconPath: ICONS.schools },
    { to: "admin/offerings", labelKey: "bottomNav.slot.offerings", iconPath: ICONS.book },
    // S-21 (nav-coherence catch) — /admin/enrollments is registrar/admin-only
    // at BOTH the FE gate and the API (route-roles.ts admin/enrollments note);
    // this slot dead-ended the dean on RoleDenied. The documented replacement
    // surface for a dean's students is the faculty-scoped student overview.
    { to: "admin/student-overview", labelKey: "bottomNav.slot.students", iconPath: ICONS.audit },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  super_admin: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "admin", labelKey: "bottomNav.slot.admin", iconPath: ICONS.shield },
    { to: "audit", labelKey: "bottomNav.slot.audit", iconPath: ICONS.audit },
    { to: "analytics", labelKey: "bottomNav.slot.analytics", iconPath: ICONS.graph },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  ta: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "classroom", labelKey: "bottomNav.slot.classroom", iconPath: ICONS.live },
    { to: "my-courses", labelKey: "bottomNav.slot.courses", iconPath: ICONS.book },
    { to: "tutor", labelKey: "bottomNav.slot.tutor", iconPath: ICONS.chat },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  content_manager: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "admin/course-content", labelKey: "bottomNav.slot.content", iconPath: ICONS.laptop },
    { to: "content-review", labelKey: "bottomNav.slot.review", iconPath: ICONS.audit },
    { to: "content-analytics", labelKey: "bottomNav.slot.analytics", iconPath: ICONS.graph },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // Support's real desk (sidenav §3.2.9): support/tickets/user-lookup/messages.
  // The old slot 1 → /admin landed the role on a RoleDenied page (route-roles
  // admin = admin/super_admin only) — its primary nav slot was dead.
  support: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "tickets", labelKey: "bottomNav.slot.tickets", iconPath: ICONS.inbox },
    { to: "user-lookup", labelKey: "bottomNav.slot.users", iconPath: ICONS.user },
    { to: "messages", labelKey: "bottomNav.slot.messages", iconPath: ICONS.chat },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // Moderator's real toolset (sidenav §3.2.10). /audit needs read:AuditLog (CASL)
  // which moderator lacks → dropped from the quick-nav.
  moderator: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "flagged-reports", labelKey: "bottomNav.slot.reports", iconPath: ICONS.shield },
    { to: "community-moderation", labelKey: "bottomNav.slot.discussions", iconPath: ICONS.community },
    { to: "help", labelKey: "bottomNav.slot.help", iconPath: ICONS.inbox },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  parent: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "parent", labelKey: "bottomNav.slot.child", iconPath: ICONS.user },
    { to: "messages", labelKey: "bottomNav.slot.messages", iconPath: ICONS.chat },
    { to: "credential", labelKey: "bottomNav.slot.credential", iconPath: ICONS.audit },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  org: [
    { to: "dashboard", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "analytics", labelKey: "bottomNav.slot.analytics", iconPath: ICONS.graph },
    { to: "catalog", labelKey: "bottomNav.slot.catalog", iconPath: ICONS.book },
    { to: "schools", labelKey: "bottomNav.slot.schools", iconPath: ICONS.schools },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // WELL-4 — counselor quick-nav (مشاور دانشگاه). Slot 0 is rewritten to the
  // role's homeRoute (counselor-inbox); slots 1-2 are the real counselor desk
  // routes route-roles.ts permits (own appointments + the open crisis queue).
  counselor: [
    { to: "counselor-inbox", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "counseling-appointments", labelKey: "bottomNav.slot.appointments", iconPath: ICONS.user },
    { to: "crisis-queue", labelKey: "bottomNav.slot.crisis", iconPath: ICONS.shield },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // Home opens the role console; profile stays in a distinct second slot.
  membership_operator: [
    { to: "profile", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "profile", labelKey: "bottomNav.slot.profile", iconPath: ICONS.user },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  membership_manager: [
    { to: "profile", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "profile", labelKey: "bottomNav.slot.profile", iconPath: ICONS.user },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  // Assessor home is the console; manager retains its distinct management tab.
  assessor: [
    { to: "profile", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "profile", labelKey: "bottomNav.slot.profile", iconPath: ICONS.user },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
  assessment_manager: [
    { to: "profile", labelKey: "bottomNav.slot.home", iconPath: ICONS.home },
    { to: "admin/assess-center", labelKey: "bottomNav.slot.assessCenter", iconPath: ICONS.audit },
    { to: "more", labelKey: "bottomNav.slot.more", iconPath: ICONS.more },
  ],
};

interface BottomNavProps {
  /** Optional override — for stories that want to test a non-current role. */
  forceRole?: RoleId;
  /** Optional override — for stories without a real router. */
  active?: string;
  /** Optional override — for stories. */
  go?: Go;
}

/**
 * Routes that should hide the bottom nav. Both /classroom and the auth
 * shell render their own full-bleed UI; the bottom nav would either
 * overlap controls (classroom toolbar) or be visually wrong (auth
 * card layout). Admin desk routes deliberately keep the nav visible
 * on mobile so phone admins can still navigate.
 */
const HIDE_ON_ROUTES = new Set([
  "classroom",
  // §12 live-class room/lobby/hub: the fixed z-[1100] nav sat ON TOP of the
  // room's bottom control bar + the schedule dialog's submit row — every tap
  // hit a nav slot and navigated away ("no buttons work").
  "live-class",
  "course-live",
  "assessment-live",
  "auth",
  "login",
  "register",
  "forgot",
  "verify-email",
  "2fa-setup",
  "onboarding",
]);

export const BottomNav: React.FC<BottomNavProps> = ({
  forceRole,
  active: activeOverride,
  go: goOverride,
}) => {
  const { t } = useTranslation("shell");
  const role = useRole();
  const currentRoute = useCurrentRoute();
  const goHook = useGo();
  const activeRoute = activeOverride ?? currentRoute.id;
  // R-NAV-4: also match the 2-segment path so faculty_admin's /admin/* slots
  // highlight correctly. For stories (activeOverride), both collapse to it.
  const activeKey =
    activeOverride ??
    (currentRoute.param ? `${currentRoute.id}/${currentRoute.param.split("/")[0]}` : currentRoute.id);
  const go = goOverride ?? goHook;
  const roleId: RoleId = forceRole ?? role.role.id;
  // نقشِ فعال از ترجیحِ ذخیره‌شدهٔ کاربر می‌آید؛ اگر کلیدی ناشناخته/خراب بود
  // (نسخهٔ قدیمیِ localStorage) نباید نوار خالی رندر شود.
  const baseSlots = Array.isArray(SLOT_TABLE[roleId]) ? SLOT_TABLE[roleId] : SLOT_TABLE.student;
  // R-NAV-4 (spec §3.4): the "home" slot (first) goes to the ROLE's home
  // (role.homeRoute), not always /dashboard. Overridden here so SLOT_TABLE
  // stays declarative and a future home-route change needs no slot edits.
  const homeRoute = ROLES[roleId]?.homeRoute ?? "dashboard";
  const slots = baseSlots.map((s, i) => (i === 0 ? { ...s, to: homeRoute } : s));

  if (HIDE_ON_ROUTES.has(activeRoute)) return null;

  return (
    <nav
      aria-label={t("bottomNav.aria")}
      className={cn(
        "fixed bottom-0 inset-x-0 z-[1100]",
        "h-16 flex items-stretch justify-between",
        "bg-[color:var(--surface)] border-t border-[color:var(--line-2)]",
        // Add the safe-area inset as extra bottom padding so the bar
        // visually sits above the iOS home indicator and Android
        // gesture pill.
        "pb-[env(safe-area-inset-bottom)]",
        // Hide on >=md (desktop uses the sidenav)
        "md:hidden",
        // Print: never
        "print:hidden",
      )}
    >
      {slots.map((slot) => {
        const isActive = slot.to === activeRoute || slot.to === activeKey;
        const label = t(slot.labelKey);
        return (
          <button
            key={slot.to}
            type="button"
            onClick={() => go(slot.to)}
            aria-current={isActive ? "page" : undefined}
            aria-label={label}
            className={cn(
              // 48×48 min touch target via the flex sizing
              "relative flex-1 min-w-[48px] min-h-[48px]",
              "flex flex-col items-center justify-center gap-0.5",
              "text-[10px] font-medium leading-none",
              "transition-colors duration-150",
              // R7.7a — accent-as-text on white nav bg fails 4.5:1 contrast.
              // The 2px top accent bar (rendered below) carries the
              // "active" visual signal; the label uses --fg for legibility.
              isActive
                ? "text-[color:var(--fg)] font-semibold"
                : "text-[color:var(--fg-mute)] hover:text-[color:var(--fg)]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40",
              "focus-visible:ring-inset",
            )}
          >
            {isActive ? (
              <span
                aria-hidden="true"
                className="absolute top-0 inset-x-3 h-[2px] rounded-b-full bg-[color:var(--accent)]"
              />
            ) : null}
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={slot.iconPath} />
            </svg>
            <span>{label}</span>
          </button>
        );
      })}
    </nav>
  );
};
BottomNav.displayName = "BottomNav";
